import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle } from 'lucide-react'

interface ToastProps {
  message: string
  visible: boolean
  onDismiss: () => void
  type?: 'success' | 'error'
  duration?: number
}

export function Toast({ message, visible, onDismiss, type = 'success', duration = 5000 }: ToastProps) {
  useEffect(() => {
    if (!visible || type !== 'success') return
    const t = setTimeout(onDismiss, duration)
    return () => clearTimeout(t)
  }, [visible, type, duration, onDismiss])

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="fixed left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[100] flex items-center gap-3 px-4 py-3.5 sm:px-5 rounded-2xl shadow-card border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
          style={{ bottom: 'max(5.5rem, calc(env(safe-area-inset-bottom) + 5.5rem))' }}
          role="alert"
        >
          {type === 'success' ? (
            <CheckCircle className="text-green-500 shrink-0" size={22} />
          ) : (
            <span className="w-5 h-5 rounded-full bg-red-500 shrink-0" />
          )}
          <span className={type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}>
            {message}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
